import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { htmlEncode } from 'htmlencode';
import { StandaloneSearchBox } from 'react-google-maps/lib/components/places/StandaloneSearchBox';
import BbButton from '@browsbox-ui/bb-button';
import { MAP_STYLES } from '../Content/ContentComponents/ContentComponentGoogleMaps';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import i18n from '../../internationalization/i18n';
import Modal from './Modal';

const propTypes = {
  address: PropTypes.string, // Google maps query value
  componentId: PropTypes.number.isRequired, // Component id
  icon: PropTypes.string,
  mapStyle: PropTypes.string, // Map style
  markerValue: PropTypes.string, // Last google maps query result
  onCancel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  submitButtonText: PropTypes.string,
  title: PropTypes.string,
};

const defaultProps = {
  address: '',
  icon: 'icon-map',
  mapStyle: MAP_STYLES.default.value,
  markerValue: '',
  submitButtonText: null,
  title: null,
};

const loadingElementStyles = {
  height: '100%',
};

const containerElementStyles = {
  height: '314px',
};
const searchBoxStyles = {
  width: '100%',
};

// Wrap react-google-maps GoogleMap component here because of required props
// that should otherwise be passed to GoogleMapsEditorModal.
class GoogleMapsSearch extends Component {
  constructor(props) {
    super(props);
    this.onSearchBoxMounted = this.onSearchBoxMounted.bind(this);
    this.onPlacesChanged = this.onPlacesChanged.bind(this);
  }

  onSearchBoxMounted(ref) {
    if (ref) {
      this.searchBox = ref;
    }
  }

  onPlacesChanged() {
    const places = this.searchBox.getPlaces();
    if (places && places.length > 0) {
      const place = places.shift();
      const {
        formatted_address: address,
        geometry: {
          location,
        },
      } = place;
      const latitude = location.lat();
      const longitude = location.lng();
      this.props.onChange({
        address,
        latitude,
        longitude,
      });
    }
  }

  render() {
    const {
      bounds,
      address,
    } = this.props;
    const mapQueryId = 'mapstyle';
    return (
      <div>
        <StandaloneSearchBox
          ref={this.onSearchBoxMounted}
          bounds={bounds}
          onPlacesChanged={this.onPlacesChanged}
        >
          <div>
            <label htmlFor={mapQueryId} className="o-bb-label">
              {i18n.t('CONTENT.address')}&nbsp;*
            </label>
            <input
              type="text"
              placeholder="Customized your placeholder"
              className="o-bb-form__input--text"
              style={searchBoxStyles}
              id={mapQueryId}
              defaultValue={address}
            />
          </div>
        </StandaloneSearchBox>
      </div>
    );
  }
}

GoogleMapsSearch.propTypes = {
  address: PropTypes.string.isRequired,
  bounds: PropTypes.any, // eslint-disable-line
  onChange: PropTypes.func.isRequired,
};

const ContentGoogleMapsSearch = GoogleMapsSearch;

class GoogleMapsEditorModal extends Component { // eslint-disable-line react/no-multi-comp
  constructor(props) {
    super(props);
    this.onOk = this.onOk.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onStyleChange = this.onStyleChange.bind(this);
    this.onAddressChange = this.onAddressChange.bind(this);
    this.state = {
      isValid: false,
      address: props.address, // from Google maps query result
      mapStyle: props.mapStyle, // from Google maps query result
      latitude: false, // from Google maps query result
      longitude: false, // from Google maps query result
      markerValue: props.markerValue, // from Google maps query result
    };
  }

  onOk() {
    const {
      componentId,
    } = this.props;
    const {
      address,
      mapStyle,
      latitude,
      longitude,
    } = this.state;
    const result = { mapStyle };
    // Only return latitude, longitude if new valid google maps query was made
    if (latitude !== false) {
      result.latitude = `${latitude}`;
      result.longitude = `${longitude}`;
      result.address = address;
      result.markerValue = address;
      result.markerTitle = htmlEncode(address.split(',').join('<br />'));
    }
    this.props.onOk(componentId, result);
  }

  onCancel(evt) {
    evt.preventDefault();
    this.props.onCancel();
  }

  onClose(evt) {
    if (evt) {
      evt.preventDefault();
    }
    this.props.onClose();
  }

  onStyleChange(evt) {
    const { value: mapStyle } = evt.target;
    const isValid = this.props.address !== '';
    this.setState({ mapStyle, isValid });
  }

  onAddressChange(value) {
    this.setState({ ...value, isValid: true });
  }

  render() {
    const {
      address,
      icon,
    } = this.props;
    const {
      isValid,
      mapStyle,
    } = this.state;

    const title = this.props.title || i18n.t('CONTENT.googleMaps');
    const submitButtonText = this.props.submitButtonText || i18n.t('CONTENT.update');
    const mapStyleId = 'mapstyle';
    const styleOption = (style) => {
      const mapStyleOption = MAP_STYLES[style];
      return (
        <option key={style} value={mapStyleOption.value}>{mapStyleOption.title}</option>
      );
    };

    return (
      <Modal
        onClose={this.onClose}
        modalClassName="o-bb-modal--small"
      >
        <div>
          <ModalHeader icon={icon} title={title} />
          <div>
            <div className="o-bb-form-field">
              <ContentGoogleMapsSearch
                loadingElement={<div style={loadingElementStyles} />}
                containerElement={<div style={containerElementStyles} />}
                address={address}
                onChange={this.onAddressChange}
              />
              { isValid && <i className="fa fa-check" /> }
            </div>
            <div className="o-bb-form-field">
              <label htmlFor={mapStyleId} className="o-bb-label">
                {i18n.t('CONTENT.theme')}
              </label>
              <span className="o-bb-form__select-wrapper">
                <select className="o-bb-form__select" value={mapStyle} onChange={this.onStyleChange} id={mapStyleId} >
                  { Object.keys(MAP_STYLES).map(styleOption) }
                </select>
              </span>
            </div>
          </div>
          <Modal.Context>
            {({ closeModal }) => (
              <ModalFooter>
                <BbButton className="c-bb-button--primary" disabled={!isValid} onClick={this.onOk} text={submitButtonText} />
                <BbButton className="c-bb-button--secondary" onClick={closeModal} text={i18n.t('CONTENT.cancel')} />
              </ModalFooter>
            )}
          </Modal.Context>
        </div>
      </Modal>
    );
  }
}

GoogleMapsEditorModal.propTypes = propTypes;
GoogleMapsEditorModal.defaultProps = defaultProps;

export default GoogleMapsEditorModal;



// WEBPACK FOOTER //
// ./src/components/Modals/GoogleMapsEditorModal.js